import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { GOOGLE_DRIVE_CONSTANTS } from '../models/google-drive.constants';

declare const gapi: any;
declare const google: any;

export interface DriveFile {
  id: string;
  name: string;
  mimeType: string;
  size?: number;
  modifiedTime?: string;
  thumbnailLink?: string;
}

@Injectable({
  providedIn: 'root',
})
export class GoogleDriveService {
  // REPLACE WITH ACTUAL Google Cloud CREDENTIALS
  private CLIENT_ID = GOOGLE_DRIVE_CONSTANTS.CLIENT_ID;
  private API_KEY = GOOGLE_DRIVE_CONSTANTS.API_KEY;

  private DISCOVERY_DOC = GOOGLE_DRIVE_CONSTANTS.DISCOVERY_DOC;
  private SCOPES = GOOGLE_DRIVE_CONSTANTS.SCOPES;

  private isSignedInSubject = new BehaviorSubject<boolean>(false);
  public isSignedIn$ = this.isSignedInSubject.asObservable();

  private gapi_loaded = false;
  private gis_loaded = false;
  private tokenClient: any;

  constructor() {
    // this.initializeGapi();
  }

  async initializeGapi(): Promise<void> {
    if (typeof gapi !== 'undefined') {
      await this.gapiLoaded();
      await this.gisLoaded();
    } else {
      setTimeout(
        () => this.initializeGapi(),
        GOOGLE_DRIVE_CONSTANTS.GAPI_INIT_RETRY_DELAY
      );
    }
  }

  async gapiLoaded(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      gapi.load('client', {
        callback: async () => {
          try {
            await gapi.client.init({
              apiKey: this.API_KEY,
              discoveryDocs: [this.DISCOVERY_DOC],
            });
            this.gapi_loaded = true;
            resolve();
          } catch (err) {
            reject(err);
          }
        },
        onerror: (err: any) => reject(err),
      });
    });
  }

  async gisLoaded(): Promise<void> {
    this.tokenClient = google.accounts.oauth2.initTokenClient({
      client_id: this.CLIENT_ID,
      scope: this.SCOPES,
      callback: (resp: any) => {
        if (resp.error !== undefined) {
          throw resp;
        }
        this.isSignedInSubject.next(true);
      },
    });
    this.gis_loaded = true;
  }


  private generateState(): string {
    const array = new Uint8Array(16);
    window.crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  }

  async signIn(): Promise<boolean> {
    const state = this.generateState();
    sessionStorage.setItem('oauth_state', state);

    return new Promise((resolve, reject) => {
      let resolved = false;

      this.tokenClient.callback = async (resp: any) => {
        const savedState = sessionStorage.getItem('oauth_state');
        if (resp.state !== savedState) {
          resolved = true;
          reject(new Error('Invalid state parameter: CSRF protection triggered'));
          return;
        }

        resolved = true;
        if (resp.error !== undefined) {
          reject(resp);
          return;
        }
        this.isSignedInSubject.next(true);
        resolve(true);
      };

      this.tokenClient.error_callback = (error: any) => {
        resolved = true;
        reject(new Error(error?.type || 'auth_error'));
      };

      const config = {
        prompt: gapi.client.getToken() === null ? 'consent' : '',
        state: state
      };

      this.tokenClient.requestAccessToken(config);

      const popupCheckInterval = setInterval(() => {
        if (resolved) {
          clearInterval(popupCheckInterval);
          return;
        }
        if (document.hasFocus()) {
          // Additional logic can be added here if needed
        }
      }, 500);

      setTimeout(() => {
        clearInterval(popupCheckInterval);
        if (!resolved) {
          resolved = true;
          reject(new Error('popup_closed'));
        }
      }, 5 * 60 * 1000);
    });
  }


  signOut(): void {
    const token = gapi.client.getToken();
    if (token !== null) {
      google.accounts.oauth2.revoke(token.access_token);
      gapi.client.setToken(GOOGLE_DRIVE_CONSTANTS.PROMPT_EMPTY);
      this.isSignedInSubject.next(false);
    }
  }

  async listFiles(): Promise<DriveFile[]> {
    try {
      const response = await gapi.client.drive.files.list({
        q: GOOGLE_DRIVE_CONSTANTS.QUERY_PDF_ONLY,
        fields: GOOGLE_DRIVE_CONSTANTS.FILE_LIST_FIELDS,
        pageSize: GOOGLE_DRIVE_CONSTANTS.DEFAULT_PAGE_SIZE,
      });
      return response.result.files || [];
    } catch (error) {
      return [];
    }
  }

  async downloadFile(fileId: string): Promise<{ blob: Blob; base64: string }> {
    try {
      // First get file metadata
      const fileResponse = await gapi.client.drive.files.get({
        fileId: fileId,
        fields: GOOGLE_DRIVE_CONSTANTS.FILE_METADATA_FIELDS,
      });

      // Download file content using the correct approach
      const response = await gapi.client.request({
        path: `${GOOGLE_DRIVE_CONSTANTS.DRIVE_FILES_ENDPOINT}/${fileId}`,
        method: GOOGLE_DRIVE_CONSTANTS.HTTP_GET,
        params: {
          alt: GOOGLE_DRIVE_CONSTANTS.ALT_MEDIA,
        },
        headers: {
          [GOOGLE_DRIVE_CONSTANTS.AUTHORIZATION_HEADER]: `${
            GOOGLE_DRIVE_CONSTANTS.BEARER_PREFIX
          } ${gapi.client.getToken().access_token}`,
        },
      });

      // The response.body should contain the binary data as a string
      let binaryString = response.body;

      // Convert binary string to ArrayBuffer
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      if (bytes.length === 0) {
        throw new Error(GOOGLE_DRIVE_CONSTANTS.ERROR_EMPTY_DATA);
      }

      // Create blob with proper MIME type
      const blob = new Blob([bytes], { type: fileResponse.result.mimeType });

      // Convert to base64
      const base64 = await this.blobToBase64(blob);

      return { blob, base64 };
    } catch (error) {
      throw new Error(
        `${GOOGLE_DRIVE_CONSTANTS.ERROR_DOWNLOAD_FAILED}: ${error.message}`
      );
    }
  }

  // Improved base64 conversion with better error handling
  private blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onloadend = () => {
        const result = reader.result as string;
        if (!result) {
          reject(new Error(GOOGLE_DRIVE_CONSTANTS.ERROR_FILE_READER_NULL));
          return;
        }

        // Check if it's a valid data URL
        if (!result.startsWith(GOOGLE_DRIVE_CONSTANTS.DATA_URL_PREFIX)) {
          reject(new Error(GOOGLE_DRIVE_CONSTANTS.ERROR_INVALID_DATA_URL));
          return;
        }

        // Additional validation for PDF files
        if (blob.type === GOOGLE_DRIVE_CONSTANTS.MIME_TYPE_PDF) {
          const base64Data = result.split(',')[1];
          if (
            !base64Data ||
            base64Data.length < GOOGLE_DRIVE_CONSTANTS.MIN_PDF_BASE64_LENGTH
          ) {
            reject(new Error(GOOGLE_DRIVE_CONSTANTS.ERROR_INVALID_PDF_BASE64));
            return;
          }

          // Basic PDF header check
          try {
            const binaryString = atob(
              base64Data.substring(
                0,
                GOOGLE_DRIVE_CONSTANTS.PDF_HEADER_CHECK_LENGTH
              )
            );
            if (!binaryString.startsWith(GOOGLE_DRIVE_CONSTANTS.PDF_HEADER)) {
              reject(
                new Error(GOOGLE_DRIVE_CONSTANTS.ERROR_INVALID_PDF_FORMAT)
              );
              return;
            }
          } catch (e) {
            reject(new Error(GOOGLE_DRIVE_CONSTANTS.ERROR_INVALID_BASE64));
            return;
          }
        }

        resolve(result);
      };

      reader.onerror = () => {
        reject(
          new Error(
            `${GOOGLE_DRIVE_CONSTANTS.ERROR_FILE_READER}: ${
              reader.error?.message || GOOGLE_DRIVE_CONSTANTS.ERROR_UNKNOWN
            }`
          )
        );
      };

      reader.readAsDataURL(blob);
    });
  }

  isSignedIn(): boolean {
    return this.isSignedInSubject.value;
  }
}
